import { useEffect } from 'react';

import { Ikon } from '../status/Ikon.tsx';

/**
 * Angre-vinduet (DD-10, DD-70): en handling som kan angres, holdes tilbake så lenge. Sammenslåingen
 * i Innspill og overgangene i rommet Hypoteser som sender svar, bruker samme tall.
 */
export const ANGRE_SEKUNDER = 5;

/** Hvor lenge en kvittering står før den går av seg selv (DD-70). */
export const KVITTERING_MS = 6000;

export interface ToastInnhold {
  tekst: string;
  /** Finnes for angrbare handlinger (DD-10): kalles når brukeren angrer innen fristen. */
  angre?: () => void;
  /** Sekunder igjen til handlingen er endelig. */
  sekunder?: number;
  /** En feil blir stående til den lukkes — brukeren skal rekke å lese hva som gikk galt og hva som fikser det. */
  feil?: boolean;
  /** Ikonet foran teksten. Standard er sammenslåingens for en angrbar handling, hake for en kvittering. */
  ikon?: string;
}

/**
 * Angre-toast (DD-01, DD-10): ikon + tekst + knapp, aldri farge alene. Sammenslåing er den
 * ene handlingen som er endelig etterpå. `assertive` bare når det er noe å angre — en
 * kvittering avbryter ikke skjermleseren midt i neste sak.
 *
 * DD-70 (produkteier 2026-09-18: «legger det seg en knapp over lagringsknappen, som må trykkes
 * bort før neste endring»): en kvittering går av seg selv etter {@link KVITTERING_MS} og står
 * nederst til høyre — aldri over «Lagre og neste». Angre-toasten har sin egen frist (DD-10),
 * og en feil blir stående til den lukkes. «Lukk» finnes fortsatt, for den som vil.
 */
export function Toast({ innhold, onLukk }: { innhold: ToastInnhold; onLukk: () => void }) {
  const gaarAvSegSelv = !innhold.angre && !innhold.feil;
  useEffect(() => {
    if (!gaarAvSegSelv) return undefined;
    const timer = setTimeout(onLukk, KVITTERING_MS);
    return () => clearTimeout(timer);
  }, [gaarAvSegSelv, innhold.tekst, onLukk]);

  return (
    <div role="status" aria-live={innhold.angre ? 'assertive' : 'polite'} aria-label="Melding" className={`panel-toast${innhold.feil ? ' panel-toast-feil' : ''}`}>
      <Ikon navn={innhold.ikon ?? (innhold.angre ? 'git-merge-outline' : innhold.feil ? 'alert-circle-outline' : 'checkmark-circle-outline')} />
      <span>{innhold.tekst}</span>
      {innhold.angre ? (
        <button type="button" className="panel-toast-knapp" onClick={innhold.angre}>
          <Ikon navn="arrow-undo-outline" /> Angre{innhold.sekunder !== undefined ? ` (${innhold.sekunder} s)` : ''}
        </button>
      ) : (
        <button type="button" className="panel-toast-knapp" onClick={onLukk}>
          Lukk
        </button>
      )}
    </div>
  );
}
